import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { take, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DateAdapter } from '@angular/material/core';
import { AvailableLanguages, allEmployeeRoles, patterns } from '@constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { environment } from '@environment';
import { Language, Office, ROLES_KEYS, User } from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { OfficeFacade } from '@state/office';
import { UserAuthFacade } from '@state/user-auth';
import { TranslateService } from '@ngx-translate/core';
import { SoftDeleteAccountModalService } from '../modals/soft-delete-account-modal/soft-delete-account-modal.service';
import { RouterFacade } from '@state/router';

@Component({
  selector: 'app-user-settings-details',
  templateUrl: './user-settings-details.component.html',
  styleUrls: ['./user-settings-details.component.scss'],
})
export class UserSettingsDetailsComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new ReplaySubject(1);

  @Input() myself: User;
  @Input() currentLang: AvailableLanguages;

  public loading$: Observable<boolean> = this.userAuthFacade.loading$;
  public languages$: Observable<Language[]> = this.commonsFacade.availableLanguages$;
  public offices$: Observable<Office[]> = this.officeFacade.offices$;
  public updateSuccess$: Observable<{ updatedUser: User }> = this.userAuthFacade.userUpdateSuccess$;
  public shouldShowOfficeField = false;
  public uncacheParam = Date.now();

  // @TODO add proper typing to FormGroup
  public form: FormGroup<any> = this.fb.group({
    email: ['', Validators.compose([Validators.required, Validators.email])],
    officeId: [null],
    languageId: ['', Validators.compose([Validators.required])],
    name: ['', Validators.compose([Validators.maxLength(200), Validators.pattern(patterns.PLNameValidator)])],
    telephoneNumber: [
      '',
      Validators.compose([
        Validators.required,
        Validators.maxLength(100),
        Validators.pattern(patterns.PhoneNumberValidator),
      ]),
    ],
  });

  public API_URL = environment.API_URL;
  public ROLES_KEYS = ROLES_KEYS;
  public userName: string = null;

  constructor(
    private readonly userAuthFacade: UserAuthFacade,
    private readonly commonsFacade: CommonsFacade,
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<Date>,
    private readonly ls: LocalStorageService,
    private readonly officeFacade: OfficeFacade,
    private readonly translateService: TranslateService,
    private readonly deleteAccountModalService: SoftDeleteAccountModalService,
    private readonly routerFacade: RouterFacade
  ) {}

  public ngOnInit(): void {
    if (!this.currentLang) {
      return;
    }

    this.languages$.pipe(take(1)).subscribe(langs => {
      const langId = langs.find(({ key }) => key === this.currentLang)?.id;
      if (langId) {
        this.form.get('languageId').setValue(langId);
      }
    });

    this.form.patchValue({
      email: this.myself?.email,
      languageId: this.myself?.languageId,
      companyId: this.myself?.companyId,
      name: this.myself?.name,
      telephoneNumber: this.myself?.telephoneNumber,
      officeId: this.myself?.office?.id || null,
    });

    this.userName = this.myself.name;
    const myRoleKey = this.myself?.role.key;
    if (myRoleKey !== ROLES_KEYS.Admin) {
      this.form.get('email')?.disable();
      this.form.get('officeId')?.disable();
    }

    // show office field for these roles (neverming if disabled)
    this.shouldShowOfficeField = allEmployeeRoles.includes(this.myself?.role.key as ROLES_KEYS);
    if (this.shouldShowOfficeField) {
      this.officeFacade.getOfficesSimpleList();
    }

    this.form
      .get('languageId')
      ?.valueChanges.pipe(withLatestFrom(this.languages$), takeUntil(this.destroy$))
      .subscribe(([langId, languages]) => {
        const pickedLanguage = languages.find(langObj => langObj.id === langId);

        if (!pickedLanguage) {
          console.error('Language picked by user does not exist in languages$ collection');
          return;
        }

        this.translateService.use(pickedLanguage.key);
        this.ls.setUserLang(pickedLanguage);
        if (pickedLanguage.key === 'pl') {
          this.dateAdapter.setLocale('pl-PL');
        }
        if (pickedLanguage.key === 'en') {
          this.dateAdapter.setLocale('en-GB');
        }
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public submitProfileForm(): void {
    const myRoleKey = this.myself?.role.key;

    const formValue = { ...this.form.value };
    // we don't want empty/null values!
    // and we're sure there are no desired falsy values (like number 0)
    Object.keys(formValue).forEach(key => {
      if (!formValue[key]) {
        delete formValue[key];
      }
    });

    this.userName = formValue.name;

    // ADMINS
    if (myRoleKey === ROLES_KEYS.Admin) {
      this.form.markAsPristine();
      this.userAuthFacade.updateAdminself(this.myself?.id as string, formValue);
      return;
    }

    // EMPLOYEES
    if ([ROLES_KEYS.Employee, ROLES_KEYS.Team_Leader, ROLES_KEYS.Field_Consultant].includes(myRoleKey as ROLES_KEYS)) {
      delete formValue.email;
      delete formValue.officeId;
      this.form.markAsPristine();
      this.userAuthFacade.updateMyself(formValue);
      return;
    }

    // FOREIGNERS, MANAGERS and rest
    delete formValue.email;
    delete formValue.officeId;
    delete formValue.surname;
    this.form.markAsPristine();
    this.userAuthFacade.updateMyself(formValue);
  }

  public deleteAccount(): void {
    this.deleteAccountModalService.open();
  }

  public exitUserSettings(): void {
    this.routerFacade.goBack();
  }
}
